/**
 * API Tab - displays API links and download options
 */

import { useState, useEffect } from 'react';
import type { WorkspaceItemType, LinkedTableVariableApi } from '@loader/shared-types';
import { type VersionOption, Input, Label } from '@loader/ui';

import {
  TableVersionSelector,
  LinkedTableVersionSelectors,
  LinksSection,
} from './shared';
import type { SourceTableVersions } from './types';

import { useRpc } from '@/lib/rpc';

interface ApiTabProps {
  itemId: string;
  itemType: WorkspaceItemType;
  versions?: VersionOption[];
  linkedTableSources?: Array<{
    id: string;
    alias: string;
    target_id: string;
    type: 'table' | 'linked_table';
  }>;
  /** Variables defined on the linked table */
  variables?: LinkedTableVariableApi[];
}

export function ApiTab({ itemId, itemType, versions = [], linkedTableSources = [], variables = [] }: ApiTabProps) {
  const rpc = useRpc();
  const [selectedVersion, setSelectedVersion] = useState('current');
  const [versionSelections, setVersionSelections] = useState<Record<string, string>>({});
  const [sourceTableVersions, setSourceTableVersions] = useState<Record<string, SourceTableVersions>>({});
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Fetch versions for linked table sources
  useEffect(() => {
    if (itemType !== 'linked_table' || linkedTableSources.length === 0) return;

    const fetchSourceTableVersions = async () => {
      setLoadingVersions(true);
      const versionsMap: Record<string, SourceTableVersions> = {};
      const initialSelections: Record<string, string> = {};

      try {
        await Promise.all(
          linkedTableSources
            .filter((source) => source.type === 'table')
            .map(async (source) => {
              try {
                let tableName = source.alias;
                try {
                  // @ts-expect-error - Hono RPC type inference limitation
                  const tableRes = await rpc.api.private.tables[':id'].$get({
                    param: { id: source.target_id },
                  });
                  if (tableRes.ok) {
                    const tableData = await tableRes.json();
                    tableName = tableData.table.name;
                  }
                } catch {
                  // Use alias as fallback
                }

                const versionsRes = await rpc.api.private.versions.$get({
                  query: { table_id: source.target_id },
                });

                if (versionsRes.ok) {
                  const versionsData = await versionsRes.json();
                  const versionsArray = versionsData.data || [];

                  const completedVersions = (versionsArray as VersionOption[])
                    .filter((v) => v.status === 'completed');

                  versionsMap[source.alias] = {
                    tableId: source.target_id,
                    tableName,
                    versions: completedVersions,
                  };

                  initialSelections[source.alias] = 'current';
                }
              } catch (error) {
                console.error(`Failed to fetch versions for ${source.alias}:`, error);
              }
            })
        );

        setSourceTableVersions(versionsMap);
        setVersionSelections(initialSelections);
      } finally {
        setLoadingVersions(false);
      }
    };

    void fetchSourceTableVersions();
  }, [itemType, linkedTableSources, rpc]);

  const handleVersionSelectionChange = (alias: string, versionId: string) => {
    setVersionSelections((prev) => ({ ...prev, [alias]: versionId }));
  };

  // Variable params state — initialized from variable defaults
  const [variableParams, setVariableParams] = useState<Record<string, string>>({});

  // Initialize variable params from defaults when variables change
  useEffect(() => {
    if (variables.length === 0) return;
    const defaults: Record<string, string> = {};
    for (const v of variables) {
      defaults[v.name] = v.default_value;
    }
    setVariableParams(defaults);
  }, [variables]);

  const handleVariableParamChange = (name: string, value: string) => {
    setVariableParams((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="space-y-4 py-2">
      {/* Version Selector (tables only) */}
      {itemType === 'table' && (
        <TableVersionSelector
          versions={versions}
          selectedVersion={selectedVersion}
          onVersionChange={setSelectedVersion}
        />
      )}

      {/* Version Selectors for Linked Table Sources */}
      {itemType === 'linked_table' && (
        <LinkedTableVersionSelectors
          sources={linkedTableSources}
          sourceTableVersions={sourceTableVersions}
          versionSelections={versionSelections}
          onVersionChange={handleVersionSelectionChange}
          isLoading={loadingVersions}
        />
      )}

      {/* Variable Parameters (linked tables with variables) */}
      {itemType === 'linked_table' && variables.length > 0 && (
        <div>
          <label className="text-muted-foreground mb-1.5 block text-xs font-medium uppercase">
            Variables
          </label>
          <div className="space-y-1.5">
            {variables.map((v) => (
              <div key={v.id} className="flex items-center gap-2">
                <Label className="text-muted-foreground w-24 shrink-0 truncate text-sm" title={v.name}>
                  {v.name}
                </Label>
                <Input
                  type={v.type === 'number' ? 'number' : v.type === 'date' ? 'date' : 'text'}
                  value={variableParams[v.name] ?? v.default_value}
                  onChange={(e) => handleVariableParamChange(v.name, e.target.value)}
                  placeholder={v.default_value || v.name}
                  className="min-w-0 flex-1 text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Shared Links Section */}
      <LinksSection
        itemId={itemId}
        itemType={itemType}
        selectedVersion={selectedVersion}
        versionSelections={versionSelections}
        variableParams={variables.length > 0 ? variableParams : undefined}
      />
    </div>
  );
}
